import { lockScroll, unlockScroll } from '@theme/utilities';

class SterlingProductTutorialMedia extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.video = this.querySelector('video');
    this.playControl = this.querySelector('[data-tutorial-play]');
    this.card = this.closest('.sterling-product-tutorial__card');
    this.dialog = this.card?.querySelector('[data-tutorial-dialog]');
    if (!this.video || !this.playControl || !this.dialog) return;
    this.inlineControls = this.video.controls;
    this.video.controls = false;
    this.playControl.hidden = false;
    this.playControl.setAttribute('aria-haspopup', 'dialog');
    this.playControl.setAttribute('aria-controls', this.dialog.id);
    this.playControl.setAttribute('aria-label', 'Open tutorial video');
    this.card.classList.add('sterling-product-tutorial__card--modal');
    this.card.addEventListener('click', (event) => {
      if (this.dialog.contains(event.target) || event.target.closest('a, button:not([data-tutorial-play]), input, select, textarea, summary')) return;
      this.open();
    }, { signal });
    this.dialog.querySelector('[data-tutorial-close]').addEventListener('click', () => this.dialog.close(), { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
    }, { signal });
    this.dialog.addEventListener('close', () => this.restore(), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    if (!this.video || !this.dialog) return;
    if (this.dialog.open) this.dialog.close();
    this.restore(false);
    this.video.controls = this.inlineControls;
    this.card.classList.remove('sterling-product-tutorial__card--modal');
  }

  open() {
    if (this.dialog.open) return;
    this.sizeDialog();
    // Reparenting a media element can reset its resource selection in browsers.
    // Restore its time after metadata loads, before requesting playback.
    this.movePlayer(this.dialog.querySelector('[data-tutorial-player]'), null, true);
    this.video.controls = true;
    this.dialog.showModal();
    lockScroll(this.dialog);
  }

  movePlayer(parent, before = null, play = false) {
    this.playbackTime = this.video.readyState ? this.video.currentTime : (this.playbackTime || 0);
    this.video.pause();
    this.mediaController?.abort();
    this.mediaController = new AbortController();
    this.video.addEventListener('loadedmetadata', () => {
      this.sizeDialog();
      this.video.currentTime = this.playbackTime;
      if (play && this.dialog.open) this.play();
    }, { once: true, signal: this.mediaController.signal });
    parent.insertBefore(this.video, before);
    this.video.load();
  }

  async play() {
    try {
      await this.video.play();
      if (!this.dialog.open) this.video.pause();
    } catch {
      // Native controls remain available if the browser refuses playback.
    }
  }

  sizeDialog() {
    if (this.video.videoWidth && this.video.videoHeight) {
      this.dialog.style.setProperty('--tutorial-aspect-ratio', this.video.videoWidth / this.video.videoHeight);
    }
  }

  restore(returnFocus = true) {
    this.movePlayer(this, this.playControl);
    this.video.controls = false;
    unlockScroll(this.dialog);
    if (returnFocus && this.isConnected) this.playControl.focus({ preventScroll: true });
  }
}

if (!customElements.get('sterling-product-tutorial-media')) {
  customElements.define('sterling-product-tutorial-media', SterlingProductTutorialMedia);
}
